import {
  Component,
  OnInit,
  OnDestroy,
  Output,
  EventEmitter,
  NgZone,
  ApplicationRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { LoginResponse } from 'src/app/domain/login/component/dto/login.dto';
import { AuthService } from 'src/app/domain/auth/service/auth.service';
import { NotificationsService, NOTIFICATIONS } from 'src/app/core/service/notifications.service';
import { PageType } from '../dto/types';
import {
  WEBSITE_PROD_ORCA,
  WEBSITE_PROD_ORCA_PRIVACY_POLICY,
  WEBSITE_PROD_ORCA_SOFTWARE,
  WEBSITE_PROD_ORCA_TERMS_OF_USE,
} from '../../../../../environments/urlService';
import { AuthBackendService } from '../../../auth/service/auth-backend.service';
import { UserFirstLoginService } from 'src/app/services/first-login/user-first-login.service';
import { Subject, takeUntil } from 'rxjs';
import { UserAutoLoginDTO } from '../set-new-password/set-new-password.component';
import { environment } from "../../../../../environments/environment";
import { CaptchaV3Service } from "../../../../services/captcha/captcha-v3.service";
import { CaptchaComponent } from "../../../shared/component/captcha/captcha.component";
import { FormBuilder, FormControl, FormGroup, Validators } from "@angular/forms";
import { EMAIL_SESSION_STORAGE_KEY } from "../login.util";
import {
  BROADCAST_CHANNEL_LOGOUT,
  BROADCAST_CHANNEL_LOGOUT_MESSAGE,
} from "../../../../core/component/sidenav/side-nav.component";

@Component({
  selector: 'app-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: [
    './login-form.component.scss',
    '../entrance-group.scss',
    '../css-webflow/normalize.css',
  ],
})
export class LoginFormComponent implements OnInit, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  @Output() newPageEvent = new EventEmitter<PageType>();

  form: FormGroup<{
    email: FormControl<string>;
    password: FormControl<string>;
  }>;

  passwVisible = false;
  inputType = 'password';
  eyeIcon = CLOSE_EYE_ICON;
  showLoginForm = false;
  private timerId: any;
  secondsLeft: number;
  captchaFirstFail = false;

  year = 2007; // https://www.youtube.com/watch?v=23Z4i2TqfM0
  PROD_ORCA_SOFTWARE = WEBSITE_PROD_ORCA_SOFTWARE;
  PROD_SERVC_PRIVACY_POLICY = WEBSITE_PROD_ORCA_PRIVACY_POLICY;
  WEBSITE_PROD_ORCA = WEBSITE_PROD_ORCA;
  PROD_ORCA_TERM_OF_USE = WEBSITE_PROD_ORCA_TERMS_OF_USE;

  showRecaptchaV2: boolean = false;
  captchaV2Token: string;
  siteV2Key: string = environment.recaptcha.siteKeyV2;
  broadcasterLogOut = new BroadcastChannel(BROADCAST_CHANNEL_LOGOUT);

  @ViewChild("captcha") captcha: CaptchaComponent;

  constructor(
    private authBackendService: AuthBackendService,
    private authService: AuthService,
    private routerService: Router,
    private notificationsService: NotificationsService,
    private captchaService: CaptchaV3Service,
    private firstLoginService: UserFirstLoginService,
    private ngZone: NgZone,
    private appRef: ApplicationRef,
    private formBuilder: FormBuilder,
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    this.year = new Date().getFullYear();
    this.firstLoginService.currentData
      .pipe(takeUntil(this._destroyStream))
      .subscribe((data) => {
        if (!data) {
          // Show login form if auto-login process has not been initiated (data is undefined).
          // if auto-login proccess has been initiated first value would be null.
          this.showLoginForm = data === undefined;
          return;
        }
        this.showLoginForm = false;
        this.emulateAutoLoginAfterSetNewPassword(data);
      });
  }

  ngOnDestroy(): void {
    this.clearTimer();
  }

  changePage(page: PageType): void {
    // Used to pass user's login to ForgotPassword page
    if (page === 'software') {
      document.location.href = this.PROD_ORCA_SOFTWARE;
    }
    if (page === 'forgotPassword') sessionStorage.setItem(EMAIL_SESSION_STORAGE_KEY, this.form.value.email);
    this.newPageEvent.emit(page);
  }

  onCaptchaV2Resolved(token: string): void {
    this.captchaV2Token = token;
  }

  onCaptchaV2Errored(err: string): void {
    console.error("captcha error", err);
    this.captcha.reset();
  }

  async onSubmit(): Promise<void> {
    this.form.disable();
    if (!(this.form.value.email && this.form.value.password)) {
      this.notificationsService.error(NOTIFICATIONS.EMPTY_PASSWORD_OR_EMAIL_ERROR);
      this.form.enable();
      return;
    }

    // before sending login request, notify all tabs that their session is deprecated.
    // Also cleans the cache and other temp variables.
    await this.logout();

    let res: LoginResponse | null = null;
    try {
      res = await this.captchaLogin(this.form.value.email, this.form.value.password);
    } catch (err) {
      this.form.enable();
      this.showRecaptchaV2 = true;
      if (this.captcha) {
        this.captcha.reset();
      }
      this.captchaV2Token = undefined;
      // If this is the first failed captcha attempt, we set the flag to true
      // and don't show any error notifications - only the captcha checkbox will appear.
      // On the second attempt, we will show a notification that the captcha is failing.
      if (err === "Captcha Unsolved") {
        if (!this.captchaFirstFail) {
          this.captchaFirstFail = true;
        } else {
          this.notificationsService.error(NOTIFICATIONS.CAPTCHA_BAD_SCORE_ERROR);
        }
        return;
      }
      throw err;
    }

    const {
      isPhoneVerified,
      isSmsVerificationNeeded,
      redirect,
      maskedPhoneNumber,
      verificationMethod,
      termsAndConditionsAccepted,
      secToNextAttempt,
    } = res;
    if (secToNextAttempt) {
      this.startTimer(secToNextAttempt);
      this.notificationsService.warning(NOTIFICATIONS.MAX_ATTEMPTS_REACHED);
      return;
    }
    if (!termsAndConditionsAccepted) {
      // If termsAndConditionsAccepted is false, we should process the acceptance of terms and conditions.
      // This will be checked on the verifySMS page, or directly on the specific terms and conditions page
      // if SMS verification is not required
      localStorage.setItem('termsAndConditions', String(true));
    }

    if (!isPhoneVerified) {
      this.changePage('setPhone');
      return;
    }

    if (isSmsVerificationNeeded) {
      this.changePage('verifySms');
      localStorage.setItem('maskedPhoneNumber', maskedPhoneNumber);
      localStorage.setItem('verificationMethod', verificationMethod);
      return;
    }

    if (!termsAndConditionsAccepted) {
      this.changePage('termsAndConditions');
      return;
    }
    this.authService.changeIsLoggedIn(true);
    this.authService.setCurrentUserType(redirect);
    this.routerService.navigate(['/entrance']);
  }

  // Before 04/07/24: after setting new password, user had to log in again.
  // After: on the user first "set password", continue without logging in.
  emulateAutoLoginAfterSetNewPassword(data: UserAutoLoginDTO): void {
    this.form.controls.email.setValue(data.login);
    this.form.controls.password.setValue(data.pass);
    this.onSubmit();
    this.firstLoginService.changeData(undefined);
  }

  async confirmLogin(promise: Promise<void>): Promise<void> {
    await promise;
  }

  flipPasswordVisibility(): void {
    this.passwVisible = !this.passwVisible;
    if (this.passwVisible) {
      this.inputType = 'text';
      this.eyeIcon = OPEN_EYE_ICON;
    } else {
      this.inputType = 'password';
      this.eyeIcon = CLOSE_EYE_ICON;
    }
  }

  // If you use the 'offline' profile on the backend and want to also disable captcha on the frontend,
  // change the 'recaptcha.enable' variable to false in the environment.ts file.
  private async captchaLogin(username: string, password: string): Promise<LoginResponse> {
    if (!environment.recaptcha.enable) {
      return this.authBackendService.login(username, password, null, null);
    }

    if (this.showRecaptchaV2 && !this.captchaV2Token) {
      this.notificationsService.error(NOTIFICATIONS.CAPTCHA_NOT_RESOLVED_ERROR);
      this.form.enable();
      return;
    }

    return this.captchaService.execute('login')
      .then(
        (token) => this.authBackendService.login(username, password, token, this.captchaV2Token)
      );
  }

  private startTimer(sec: number): void {
    this.clearTimer();
    if (sec <= 0) {
      return;
    }

    this.secondsLeft = sec;
    this.form.disable();
    this.ngZone.runOutsideAngular(() => {
      this.timerId = setInterval(() => {
        if (this.secondsLeft >= 0) {
          this.secondsLeft--;
        } else {
          this.clearTimer();
        }
        this.appRef.tick();
      }, 1000);
    });
  }

  private clearTimer(): void {
    this.form.enable();
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private initForm(): void {
    this.form = this.formBuilder.group({
      email: new FormControl<string>('', { validators: [ Validators.email ] }),
      password: new FormControl<string>(''),
    });
  }

  private async logout(): Promise<void> {
    await this.authBackendService.logout();
    this.broadcasterLogOut.postMessage(BROADCAST_CHANNEL_LOGOUT_MESSAGE);
  }
}

const CLOSE_EYE_ICON = 'password-eye-closed.svg; icon-size-1em';
const OPEN_EYE_ICON = 'password-eye-opened.svg; icon-size-1em';
